import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import TomSelect from "tom-select";
import Swal from "sweetalert2";

const IzinForm = forwardRef((props, ref) => {
  const {
    defaultJenis,
    ijinOptions = [],
    keterangan,
    setKeterangan,
    isModalOpen,
    className = "",
  } = props;

  const tomSelectRef = useRef(null);
  const tomInstance = useRef(null);
  const [tanggalIzin, setTanggalIzin] = useState([]); // array tanggal izin per hari
  const [tanggalMasukKembali, setTanggalMasukKembali] = useState('');
  const [jumlahHari, setJumlahHari] = useState(1);

  const [ijinType, setIjinType] = useState(
    typeof defaultJenis === "object"
      ? defaultJenis?.id
      : defaultJenis || ""
  );

  // ===============================
  // HELPER AMBIL KARYAWAN ID
  // ===============================
  const getKaryawanId = () => {
    try {
      const raw = localStorage.getItem("data_karyawan");
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      return parsed?.id || null;
    } catch (err) {
      console.log("Gagal baca data_karyawan:", err);
      return null;
    }
  };

  // ===============================
  // HELPER PERIODE (sesuai punyamu)
  // ===============================
  const getPeriod = () => {
    const now = new Date();

    let month = now.getMonth();
    let year = now.getFullYear();

    if (now.getDate() >= 21) month += 1;

    if (month > 11) {
      month = 0;
      year += 1;
    }

    return `${new Date(year, month).toLocaleString('en-US', {
      month: 'long'
    })} ${year}`;
  };

  // ===============================
  // 👉 FUNGSI SUBMIT KE API
  // ===============================
  useImperativeHandle(ref, () => ({
    submitIzin: async () => {
      try {
        const karyawanId = getKaryawanId();

        if (!karyawanId) {
          await Swal.fire({
            icon: "error",
            title: "Data Tidak Ditemukan",
            text: "Data karyawan tidak ditemukan, silakan login ulang",
          });
          return false;
        }

        if (!keterangan) {
          await Swal.fire({
            icon: "warning",
            title: "Keterangan Kosong",
            text: "Keterangan wajib diisi",
          });
          return false;
        }

        if (tanggalIzin.some(t => !t)) {
          await Swal.fire({
            icon: "warning",
            title: "Tanggal Belum Lengkap",
            text: "Semua tanggal izin wajib diisi",
          });
          return false;
        }

        if (!tanggalMasukKembali) {
          await Swal.fire({
            icon: "warning",
            title: "Tanggal Masuk Kosong",
            text: "Tanggal masuk kembali wajib diisi",
          });
          return false;
        }

        // Konfirmasi dulu sebelum kirim
        const confirm = await Swal.fire({
          icon: "question",
          title: "Ajukan Izin?",
          text: "Pastikan data yang diisi sudah benar",
          showCancelButton: true,
          confirmButtonText: "Ya, Ajukan",
          cancelButtonText: "Batal",
        });

        if (!confirm.isConfirmed) return false;

        const penggantiId = tomInstance.current?.getValue() || "";

        let ttdBase64 = "";
        if (canvasRef.current) {
          ttdBase64 = canvasRef.current.toDataURL("image/png");
        }

        const formData = new FormData();

        formData.append("karyawan_id", karyawanId);
        formData.append("absen_id", ijinType);
        formData.append("periode", getPeriod());

        formData.append("tanggal", tanggalIzin[0]);
        formData.append("jumlah_hari", jumlahHari);
        formData.append("tanggal_ijin", tanggalIzin.join(", "));
        formData.append("tanggal_masuk", tanggalMasukKembali);

        formData.append("masuk", penggantiId);
        formData.append("nama", keterangan);

        if (ttdBase64) {
          formData.append("ttd_pemohon", ttdBase64);
        }

        formData.append("telat", "1");
        formData.append("bolos", "0");

        // Tampilkan success langsung (Background processing)
        Swal.fire({
          icon: "success",
          title: "Berhasil!",
          text: "Pengajuan izin sedang diproses...",
          timer: 1500,
          showConfirmButton: false,
        });

        // Proses API di background
        fetch("https://jne-cilacap.com/slip/public/api/absen", {
            method: "POST",
            body: formData,
        }).then(async (res) => {
            if (!res.ok) throw new Error(await res.text());
            console.log("Submit izin sukses (background)");
        }).catch(err => {
            console.error("Gagal kirim izin (background):", err.message);
            Swal.fire({ icon: "error", title: "Gagal Mengirim", text: "Proses izin gagal: " + err.message });
        });

        return true;

      } catch (err) {
        console.error("Gagal kirim izin:", err);

        await Swal.fire({
          icon: "error",
          title: "Gagal Mengirim",
          text: err.message,
        });

        return false;
      }
    },
  }));

  // ===============================
  // STYLE CENTRAL
  // ===============================
  const styles = {
    field: {
      marginBottom: 16,
    },

    label: {
      display: "block",
      marginBottom: 8,
      fontWeight: 500,
    },

    input: {
      width: "100%",
      padding: "12px",
      borderRadius: 12,
      border: "1.5px solid #ddd",
      fontSize: 15,
      backgroundColor: "#fff",
      outline: "none",
      boxSizing: "border-box",
    },

    select: {
      width: "100%",
      height: 50,
      padding: "12px",
      borderRadius: 12,
      border: "1.5px solid #ddd",
      fontSize: 15,
      backgroundColor: "#fff",
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: "right 12px center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "12px",
      boxSizing: "border-box",
    },
  };

  const handleJumlahHariChange = (e) => {
    let value = e.target.value;

    // simpan mentah dulu (biar input tetap bisa diketik)
    setJumlahHari(value);

    // kalau kosong / bukan angka / <= 0 → reset
    if (!value || isNaN(value) || parseInt(value) <= 0) {
      setTanggalIzin([]);
      return;
    }

    const jumlah = parseInt(value);

    setTanggalIzin((prev) => {
      const baru = [...prev];

      // kalau kurang → tambah slot
      if (baru.length < jumlah) {
        return [
          ...baru,
          ...Array(jumlah - baru.length).fill('')
        ];
      }

      // kalau kebanyakan → potong
      return baru.slice(0, jumlah);
    });
  };


  // Update tanggal per hari ke-n
  const handleTanggalChange = (index, value) => {
    const newTanggal = [...tanggalIzin];
    newTanggal[index] = value;
    setTanggalIzin(newTanggal);
  };


  // ===============================
  // LOAD DATA KARYAWAN
  // ===============================
  const loadKaryawan = () => {
    try {
      const raw = localStorage.getItem("karyawan_pengganti_list");
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.log("Gagal parse karyawan:", err);
      return [];
    }
  };

  // ===============================
  // INIT TOM SELECT
  // ===============================
  useEffect(() => {
    if (!tomSelectRef.current) return;

    const dataKaryawan = loadKaryawan();

    tomInstance.current = new TomSelect(tomSelectRef.current, {
      valueField: "id",
      labelField: "nama",
      searchField: "nama",
      placeholder: "Pilih karyawan pengganti",

      options: dataKaryawan,

      render: {
        option: (data, escape) => `
          <div style="padding:6px 8px">
            <strong>${escape(data.nama)}</strong>
          </div>
        `,

        item: (data, escape) => `
          <div>${escape(data.nama)}</div>
        `,
      },
    });

    return () => {
      tomInstance.current?.destroy();
    };
  }, []);

  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const ctxRef = useRef(null);

  // Setup canvas untuk tanda tangan
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 3;
      ctxRef.current = ctx;

      // Responsif canvas
      const resizeCanvas = () => {
        const { width } = canvas.getBoundingClientRect();
        canvas.width = width * window.devicePixelRatio;
        canvas.height = 150 * window.devicePixelRatio;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      };
      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);
      return () => window.removeEventListener('resize', resizeCanvas);
    }
  }, [isModalOpen]);

  const startDrawing = (e, isTouch = false) => {
    isDrawing.current = true;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(x, y);
  };

  // Tambahkan useEffect baru ini (letakkan di bawah useEffect canvas setup)
  useEffect(() => {
    if (isModalOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [isModalOpen]);

  const draw = (e, isTouch = false) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
  };

  const stopDrawing = () => {
    isDrawing.current = false;
  };

  const clearSignature = () => {
    if (!canvasRef.current) return; // Tambahkan pengecekan ini
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  // ===============================
  // RENDER
  // ===============================

  const configJenis = {
    "5": {
      hari: 1,
      placeholder: "Contoh: Ada keperluan mendadak keluarga"
    },

    "10": {
      hari: 1,
      placeholder: "Contoh: Ada Acara keluarga dekat"
    },

    "14": {
      hari: 0.5,
      placeholder: "Contoh: Mengambil Raport Anak"
    },

    "20": {
      hari: 90,
      placeholder: "Contoh: Sudah mendekati HPL"
    },

    "21": {
      hari: 3,
      placeholder: "Contoh: Menejelang Akad Nikah"
    },

    "22": {
      hari: 1,
      placeholder: "Contoh: Mbah meninggal dunia"
    },

    "23": {
      hari: 1,
      placeholder: "Contoh: Kaka kandung menikah"
    }
  };

  const selectedOption = defaultJenis || ijinOptions.find(opt => opt.id === ijinType);

  // ambil config dari mapping
  const jenisConfig = configJenis[ijinType] || {
    hari: 1,
    placeholder: `Jelaskan alasan ${selectedOption?.nama || "izin"}`
  };

  const hariDefault = jenisConfig.hari;
  const editableHari = ijinType == 5 || ijinType == 10;
  const placeholderKeterangan = jenisConfig.placeholder;

  useEffect(() => {
    if (!hariDefault) return;

    setJumlahHari(hariDefault);

    setTanggalIzin((prev) => {
      const jumlah = parseInt(hariDefault);

      if (!prev || prev.length === 0) {
        return Array(jumlah).fill('');
      }

      if (prev.length < jumlah) {
        return [...prev, ...Array(jumlah - prev.length).fill('')];
      }

      return prev.slice(0, jumlah);
    });
  }, [hariDefault]);

  return (
    <div
      className={className}
      style={{
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* JENIS IJIN */}
      <div style={styles.field}>
        <label style={styles.label}>
          Jenis :
        </label>

        {selectedOption ? (
          <p
            style={{
              margin: 0,
              padding: "12px",
              fontSize: 15,
              color: "#333",
              backgroundColor: "#f9f9f9",
              borderRadius: 12,
              border: "1px solid #e0e0e0",
            }}
          >
            {selectedOption.nama}
            {selectedOption.keterangan && ` - ${selectedOption.keterangan}`}
          </p>
        ) : (
          <p
            style={{
              margin: 0,
              padding: "12px",
              fontSize: 15,
              color: "#999",
              backgroundColor: "#f9f9f9",
              borderRadius: 12,
              border: "1px solid #e0e0e0",
            }}
          >
            Tidak ada jenis izin dipilih
          </p>
        )}
      </div>

      {/* KETERANGAN */}
      <div style={styles.field}>
        <label style={styles.label}>Keterangan:</label>

        <textarea
          value={keterangan}
          onChange={(e) => setKeterangan(e.target.value)}
          placeholder={placeholderKeterangan}
          rows={4}
          style={styles.input}
        />

      </div>

      {/* JUMLAH HARI */}
      <div style={styles.field}>
        <label style={styles.label}>Jumlah Hari:</label>

        <input
          type="number"
          min="1"
          value={jumlahHari}
          onChange={handleJumlahHariChange}

          disabled={!editableHari}

          placeholder={
            editableHari
              ? "Masukkan jumlah hari izin"
              : "Jumlah hari sudah ditentukan sistem"
          }

          style={{
            ...styles.input,
            backgroundColor: editableHari ? "#fff" : "#f1f5f9",
            cursor: editableHari ? "text" : "not-allowed",
            opacity: editableHari ? 1 : 0.8,
          }}
        />

      </div>

      {/* TANGGAL DINAMIS */}
      {jumlahHari > 0 &&
        Array.from({ length: Math.ceil(jumlahHari) }).map((_, index) => (
          <div key={index} style={styles.field}>
            <label style={styles.label}>
              Tanggal Ijin Hari ke-{index + 1}:
            </label>

            <input
              type="date"
              value={tanggalIzin[index] || ""}
              onChange={(e) =>
                handleTanggalChange(index, e.target.value)
              }
              style={styles.input}
            />
          </div>
        ))}

      {/* TANGGAL MASUK KEMBALI */}
      {jumlahHari > 0 && (
        <div style={styles.field}>
          <label style={styles.label}>
            Tanggal Masuk Kembali:
          </label>

          <input
            type="date"
            value={tanggalMasukKembali}
            onChange={(e) => setTanggalMasukKembali(e.target.value)}
            style={styles.input}
          />
        </div>
      )}

      {/* PENGGANTI */}
      <div style={styles.field}>
        <label style={styles.label}>
          Tugas Digantikan Oleh:
        </label>

        <select ref={tomSelectRef} style={{ width: "100%" }}>
          <option value="">Pilih karyawan pengganti</option>
        </select>
      </div>

      {/* SIGNATURE */}
      <div style={styles.field}>
        <label style={styles.label}>Tanda Tangan:</label>

        <canvas
          ref={canvasRef}
          width={300}
          height={150}
          style={{
            border: "1.5px dashed #ccc",
            borderRadius: 12,
            width: "100%",
            height: 150,
            backgroundColor: "#f9f9f9",
            touchAction: "none",
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={(e) => {
            e.preventDefault();
            startDrawing(e, true);
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            draw(e, true);
          }}
          onTouchEnd={stopDrawing}
        />

        <button
          onClick={clearSignature}
          style={{
            marginTop: 8,
            color: "#dc2626",
            background: "none",
            border: "none",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Hapus Tanda Tangan
        </button>
      </div>
    </div>
  );
});

export default IzinForm;
